import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import { HxBaseControl } from '../../base';
import { HxIcon } from '../icon/icon';
import { SideNavBorderRadius, SideNavMode, SideNavPosition } from './side-nav-types';

/**
 * # Harmony Side Navigation Component
 *
 * A collapsible, responsive navigation container with full keyboard accessibility and state persistence.
 *
 * ## Features
 *
 * - **Collapsible icon-only view**: Toggle between expanded labels and icon-only navigation
 * - **State persistence**: Optional LocalStorage integration to remember user preferences
 * - **Smooth animations**: Customizable transition duration for expand/collapse
 * - **Flexible header**: Accept custom content via ng-content projection with `hx-header-content` attribute
 * - **Built-in toggle button**: Optional toggle button positioned at the edge between drawer and content
 * - **WCAG 2.2 AA compliant**: Full keyboard navigation and screen reader support
 * - **Responsive design**: Adapts seamlessly to different screen sizes
 * - **Material Design drawer modes**: side, over, and push modes with positioning
 *
 * ## Usage Example
 *
 * ```html
 * <hx-side-nav [mode]="'side'" [position]="'start'" [opened]="true">
 *   *
 *   <div hx-header-content style="display: flex; align-items: center; gap: 12px">
 *     <hx-icon svgIcon="dashboard"></hx-icon>
 *     <span>Navigation</span>
 *   </div>
 *
 *   * <hx-tree [nodes]="navigationNodes" [collapsed]="sideNav.getCollapsedState()"></hx-tree>
 * </hx-side-nav>
 * ```
 */
@Component({
  selector: 'hx-side-nav',
  standalone: true,
  imports: [CommonModule, HxIcon],
  templateUrl: './side-nav.html',
  styleUrls: ['./side-nav.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-drawer-container]': 'true',
    '[class.hx-drawer-container--collapsed]': 'collapsedState()',
    '[class.hx-drawer-container--opened]': 'isOpened()',
    '[class.hx-drawer-container--closed]': '!isOpened()',
    '[class.hx-drawer-container--over]': 'isOverMode()',
    '[class.hx-drawer-container--push]': 'isPushMode()',
    '[class.hx-drawer-container--side]': 'isSideMode()',
    '[class.hx-drawer-container--start]': 'isStartPosition()',
    '[class.hx-drawer-container--end]': 'isEndPosition()',
    '[class]': 'customClassName()',
    '[style]': 'dynamicStyles()',
  },
})
export class HxSideNav extends HxBaseControl {
  /**
   * Animation duration in milliseconds for expand/collapse transitions
   *
   * Default: `300`
   */
  readonly animationDuration = input<number>(300);

  /**
   * Whether the side-nav starts in expanded state
   *
   * Default: `true`
   */
  readonly defaultExpanded = input<boolean>(true);

  /**
   * Header icon name (SVG icon)
   *
   * **Deprecated:** Use custom header content via `[hx-header-content]` attribute instead. Default: `'file-text'`
   */
  readonly headerIcon = input<string>('file-text');

  /**
   * Header title text
   *
   * **Deprecated:** Use custom header content via `[hx-header-content]` attribute instead. Default: `'SOAP Notes'`
   */
  readonly headerTitle = input<string>('SOAP Notes');

  /**
   * Sidebar width in collapsed state (icon-only view)
   *
   * CSS size value (e.g., '108px', '5rem'). When autoSize is true, this value is ignored. Default: `'fit-content'`
   */
  readonly collapsedWidth = input<string>('fit-content');

  /**
   * Sidebar width in expanded state
   *
   * CSS size value (e.g., '250px', '16rem'). When autoSize is true, this value is ignored. Default: `'fit-content'`
   */
  readonly expandedWidth = input<string>('fit-content');

  /**
   * Sidebar height
   *
   * CSS size value (e.g., '100%', '100vh') Default: `'100%'`
   */
  readonly height = input<string>('100%');

  /**
   * Sidebar position on screen: 'start' (left) or 'end' (right)
   *
   * Default: `'start'`
   */
  readonly position = input<SideNavPosition>('start');

  /**
   * Sidebar behavior mode
   *
   * - `'side'`: Sidebar is always visible, pushes content
   * - `'over'`: Sidebar overlays content
   * - `'push'`: Sidebar pushes content when expanded
   *
   * Default: `'side'`
   */
  readonly mode = input<SideNavMode>('side');

  /**
   * Whether the sidebar is opened (for 'over' and 'push' modes)
   *
   * Default: `true`
   */
  readonly opened = input<boolean>(true);

  /**
   * Prevent closing the sidebar via click/escape (for 'over' mode)
   *
   * Default: `false`
   */
  readonly disableClose = input<boolean>(false);

  /**
   * Automatically focus the first focusable element when opened
   *
   * Default: `true`
   */
  readonly autoFocus = input<boolean>(true);

  /**
   * Automatically adjust width based on content. When true, ignores expandedWidth and collapsedWidth.
   *
   * Default: `true`
   */
  readonly autoSize = input<boolean>(true);

  /**
   * Show the built-in toggle button in the header
   *
   * Default: `true`
   */
  readonly showToggle = input<boolean>(true);

  /**
   * Controlled collapsed state input
   *
   * Default: `undefined` (uncontrolled mode)
   */
  readonly isCollapsed = input<boolean | undefined>(undefined);

  readonly showHeader = input<boolean>(true);

  readonly showFooter = input<boolean>(false);

  /**
   * Border radius configuration - determines which corners are rounded
   *
   * - `'all'`: All four corners are rounded (default)
   * - `'left'`: Only left corners (top-left and bottom-left) are rounded
   * - `'right'`: Only right corners (top-right and bottom-right) are rounded
   * - `'none'`: No corners are rounded (square corners)
   *
   * Default: `'all'`
   */
  readonly borderRadius = input<SideNavBorderRadius>('all');

  /**
   * Internal collapsed state (uncontrolled mode only)
   *
   * @private
   */
  private readonly _uncontrolledState = signal<boolean>(false);

  /** Internal opened state (used for over/push modes) */
  private readonly _openedState = signal<boolean>(true);
  private _isInitialized = false;

  constructor() {
    super();

    effect(() => {
      if (this._isInitialized) {
        return;
      }

      // Initialize opened state from input
      this._openedState.set(this.opened());

      // Initialize uncontrolled state from defaultExpanded (only if not in controlled mode)
      if (this.isCollapsed() === undefined) {
        this._uncontrolledState.set(!this.defaultExpanded());
      }

      this._isInitialized = true;
    });
  }

  // Mode checks
  readonly isOverMode = computed(() => this.mode() === 'over');
  readonly isPushMode = computed(() => this.mode() === 'push');
  readonly isSideMode = computed(() => this.mode() === 'side');
  readonly isStartPosition = computed(() => this.position() === 'start');
  readonly isEndPosition = computed(() => this.position() === 'end');
  readonly isOpened = computed(() => this._openedState());

  /** Final collapsed state - prioritizes controlled input over uncontrolled internal state */
  readonly collapsedState = computed(() => {
    const controlled = this.isCollapsed();
    return controlled !== undefined ? controlled : this._uncontrolledState();
  });

  // Computed dynamic styles
  readonly dynamicStyles = computed(() => {
    const collapsed = this.collapsedState();
    const width = collapsed ? this.collapsedWidth() : this.expandedWidth();
    const height = this.height();
    const duration = this.animationDuration();

    const styles: Record<string, string> = {
      '--hx-side-nav-width': width,
      '--hx-side-nav-height': height,
      '--hx-side-nav-duration': `${duration}ms`,
    };

    return Object.entries(styles)
      .map(([key, value]) => `${key}:${value}`)
      .join(';');
  });

  /**
   * Custom CSS classes provided via className input (from HxBaseControl) Applied to the host element for component
   * theming
   */
  readonly customClassName = computed(() => {
    return this.cssClass() || '';
  });

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.isOverMode() && !this.disableClose() && this.isOpened()) {
      keyboardEvent.preventDefault();
      this._closeDrawer();
    }
  }

  /**
   * Toggle collapsed/expanded state Only works in uncontrolled mode.
   *
   * @protected
   */
  protected _toggleCollapsed(): void {
    if (this.isCollapsed() !== undefined) {
      console.warn('HxSideNav: In controlled mode. Update [isCollapsed] input instead.');
      return;
    }
    this._uncontrolledState.set(!this._uncontrolledState());
  }

  /**
   * Close the drawer (for 'over' and 'push' modes)
   *
   * @protected
   */
  protected _closeDrawer(): void {
    if (this.disableClose() && this.isOpened()) {
      return;
    }
    this._openedState.set(false);
  }

  /**
   * Toggle opened/closed state (over/push modes)
   *
   * @protected
   */
  protected _toggleOpened(): void {
    if (this.disableClose() && this.isOpened()) {
      return;
    }
    this._openedState.set(!this._openedState());
  }

  /**
   * Handle toggle button behavior
   *
   * @protected
   */
  protected _handleToggle(): void {
    if (this.isOverMode() || this.isPushMode()) {
      this._toggleOpened();
      return;
    }
    this._toggleCollapsed();
  }

  /** Open the drawer (over/push) or expand the sidebar (side). */
  open(): void {
    if (this.isOverMode() || this.isPushMode()) {
      this._openedState.set(true);
      return;
    }
    if (this.isCollapsed() !== undefined) {
      console.warn('HxSideNav: In controlled mode. Update [isCollapsed] input instead.');
      return;
    }
    this._uncontrolledState.set(false);
  }

  /** Close the drawer (over/push) or collapse the sidebar (side). */
  close(): void {
    if (this.isOverMode() || this.isPushMode()) {
      this._closeDrawer();
      return;
    }
    if (this.isCollapsed() !== undefined) {
      console.warn('HxSideNav: In controlled mode. Update [isCollapsed] input instead.');
      return;
    }
    this._uncontrolledState.set(true);
  }

  /** Toggle the drawer (over/push) or collapse/expand (side). */
  toggle(): void {
    this._handleToggle();
  }

  /** Get current collapsed state */
  getCollapsedState(): boolean {
    return this.collapsedState();
  }
}
